import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialKpiRail, type EditorialKpiItem } from '../../report/editorial';

export const TopographySection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  // QA 2026-06-10 — Topography previously echoed the FULL
  // `testFitAnalysis.siteConstraints` paragraph verbatim, rendering
  // byte-identical to Parcel Overview (dedup similarity 1.0). It now renders
  // the dedicated `testFitAnalysis.topography` narrative (TEST_FIT_SCHEMA
  // field) plus the datasets.land elevation/slope stats; when only the
  // constraints text exists (legacy reports), a one-line cross-reference
  // replaces the verbatim echo.
  const land = report.datasets?.land;
  // Defensive read: the `topography` narrative is additive on the test-fit
  // schema; legacy report payloads (and older type snapshots) omit it.
  const topographyNarrative = (report.testFitAnalysis as { topography?: string } | undefined)
    ?.topography?.trim();
  const hasConstraints = Boolean(report.testFitAnalysis?.siteConstraints?.trim());
  const slope = land?.slope;
  const elevation = land?.elevation;
  const hasData = slope !== undefined || elevation !== undefined || !!topographyNarrative || hasConstraints;
  // W1c editorial port — the two-tile stat grid (hand-rolled small-caps
  // label + app mono utility value) becomes an EditorialKpiRail: themed
  // top/bottom rules, mono-run keys + tabular values via --report-body-font
  // (no app mono face). Values keep the exact rendered text ("25 m" / "2%");
  // cells with absent values are dropped by the rail, never fabricated.
  const railItems: EditorialKpiItem[] = [
    {
      key: 'Elevation',
      value: elevation !== undefined && isFinite(elevation) ? `${elevation.toFixed(0)} m` : null,
    },
    { key: 'Slope', value: slope !== undefined ? `${slope}%` : null },
  ];
  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      <EditorialKpiRail theme={theme} items={railItems} />
      {/* Standard body styling (text-sm, non-italic) — the previous italic
          text-xs echo rendered visibly smaller than sibling section bodies. */}
      {topographyNarrative && (
        <p className="text-sm" style={{ color: roleColor(theme, 'body') }}>{topographyNarrative}</p>
      )}
      {!topographyNarrative && hasConstraints && (
        <p className="text-sm" style={{ color: roleColor(theme, 'muted') }}>
          Constraint detail (wetlands, flood zone, slope) is presented in the Parcel &amp; Net Buildable Area section.
        </p>
      )}
    </SectionShell>
  );
};
